/* Cii product page behaviour: gallery, variant chips with live low-stock
   labels, price + button + sticky bar sync, back-in-stock notify form.
   Add-to-cart itself is Dawn's <product-form> (product-form.js). */

if (!customElements.get('cii-product')) {
  customElements.define(
    'cii-product',
    class CiiProduct extends HTMLElement {
      connectedCallback() {
        const dataEl = this.querySelector('[data-cii-product-json]');
        if (!dataEl) return;
        try {
          this.data = JSON.parse(dataEl.textContent);
        } catch (e) {
          return;
        }

        this.variantInput = this.querySelector('[data-cii-variant-id]');
        this.atcButton = this.querySelector('[data-cii-atc]');
        this.atcLabel = this.querySelector('[data-cii-atc-label]');
        this.priceCurrent = this.querySelector('[data-cii-price-current]');
        this.priceCompare = this.querySelector('[data-cii-price-compare]');
        this.saleBadge = this.querySelector('[data-cii-sale-badge]');
        this.saveLine = this.querySelector('[data-cii-save]');
        this.notify = this.querySelector('[data-cii-notify]');
        this.sticky = this.querySelector('[data-cii-sticky]');
        this.optionRows = Array.from(this.querySelectorAll('[data-cii-option-row]'));

        const current = this.data.variants.find((v) => String(v.id) === String(this.data.selectedId));
        this.selectedOptions = current ? current.options.slice() : [];

        this.bindChips();
        this.bindGallery();
        this.bindSticky();
        this.bindNotify();
        this.updateUI();
      }

      /* ---------- money ---------- */
      formatMoney(cents) {
        const format = this.data.moneyFormat || 'R {{amount}}';
        const amount = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format
          .replace(/\{\{\s*amount\s*\}\}/, amount)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100).toString())
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, (cents / 100).toFixed(2).replace('.', ','));
      }

      /* ---------- variant logic ---------- */
      findVariant(options) {
        return this.data.variants.find((v) => v.options.every((o, i) => o === options[i]));
      }

      get selectedVariant() {
        if (this.data.hasOnlyDefaultVariant) return this.data.variants[0];
        return this.findVariant(this.selectedOptions);
      }

      bindChips() {
        this.optionRows.forEach((row) => {
          const idx = parseInt(row.dataset.optionIndex, 10);
          row.querySelectorAll('[data-cii-chip]').forEach((chip) => {
            chip.addEventListener('click', () => {
              this.selectedOptions[idx] = chip.dataset.value;
              // a colour picked with a size it has none of moves to its first size in stock (Hamza, 2026-10-22: Charcoal
              // on size 16 showed sold out though Charcoal had six sizes in stock)
              const chosen = this.findVariant(this.selectedOptions);
              if (!(chosen && chosen.available)) {
                const near = this.data.variants.find((v) => v.available && v.options.slice(0, idx + 1).every((o, i) => o === this.selectedOptions[i]));
                if (near) this.selectedOptions = near.options.slice();
              }
              this.updateUI();
            });
          });
        });
      }

      updateUI() {
        const variant = this.selectedVariant;

        // Chip states, relative to the other selected options.
        this.optionRows.forEach((row) => {
          const idx = parseInt(row.dataset.optionIndex, 10);
          row.querySelectorAll('[data-cii-chip]').forEach((chip) => {
            const probe = this.selectedOptions.slice();
            probe[idx] = chip.dataset.value;
            const match = this.findVariant(probe);
            // the first option (the colour) is out only when none of its variants can be bought; the later ones (the
            // sizes) are out against what is chosen before them (2026-10-22, the Plain Dome Toppie's Charcoal)
            const first = idx === 0 && this.selectedOptions.length > 1;
            const available = first ? this.data.variants.some((v) => v.available && v.options[0] === chip.dataset.value) : !!(match && match.available);
            chip.classList.toggle('on', this.selectedOptions[idx] === chip.dataset.value);
            chip.classList.toggle('out', !available);
            chip.setAttribute('aria-pressed', String(this.selectedOptions[idx] === chip.dataset.value));
            const low = chip.querySelector('[data-cii-chip-low]');
            if (low) {
              const isLow = !first && available && match && match.tracked && !match.policyContinue && match.qty > 0 && match.qty <= 3;
              low.hidden = !isLow;
              low.textContent = isLow ? match.qty + ' left' : '';
            }
          });
        });

        if (!variant) {
          if (this.atcButton) {
            this.atcButton.disabled = true;
            if (this.atcLabel) this.atcLabel.textContent = 'Unavailable';
          }
          if (this.notify) this.notify.hidden = true;
          return;
        }

        // Hidden input + URL.
        if (this.variantInput) this.variantInput.value = variant.id;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        } catch (e) {
          /* no-op */
        }

        // Price block.
        const onSale = variant.cap > variant.price;
        if (this.priceCurrent) this.priceCurrent.textContent = this.formatMoney(variant.price);
        if (this.priceCompare) {
          this.priceCompare.hidden = !onSale;
          this.priceCompare.textContent = onSale ? this.formatMoney(variant.cap) : '';
        }
        if (this.saleBadge) this.saleBadge.hidden = !onSale;
        if (this.saveLine) {
          this.saveLine.hidden = !onSale;
          this.saveLine.textContent = onSale ? 'Save ' + this.formatMoney(variant.cap - variant.price) : '';
        }

        // Add to cart button (price lives in the button).
        if (this.atcButton) {
          this.atcButton.disabled = !variant.available;
          if (this.atcLabel) {
            this.atcLabel.textContent = variant.available
              ? 'Add to cart · ' + this.formatMoney(variant.price)
              : 'Sold out';
          }
        }

        // Sticky bar.
        if (this.sticky) {
          const stickyVariant = this.sticky.querySelector('[data-cii-sticky-variant]');
          const stickyPrice = this.sticky.querySelector('[data-cii-sticky-price]');
          const stickyBtn = this.sticky.querySelector('[data-cii-sticky-btn]');
          const stickyThumb = this.sticky.querySelector('[data-cii-sticky-thumb]');
          if (stickyVariant && !this.data.hasOnlyDefaultVariant) {
            stickyVariant.textContent = ' · ' + variant.title;
          }
          if (stickyPrice) stickyPrice.textContent = this.formatMoney(variant.price);
          if (stickyBtn) {
            stickyBtn.disabled = !variant.available;
            stickyBtn.textContent = variant.available ? 'Add to cart' : 'Sold out';
          }
          if (stickyThumb && variant.thumb) stickyThumb.src = variant.thumb;
        }

        // Variant media.
        if (variant.mediaId) this.activateMedia(String(variant.mediaId));

        // Back in stock form.
        if (this.notify) {
          this.notify.hidden = variant.available;
          this.notify.dataset.variantId = variant.id;
          this.notify.dataset.variantLabel = this.data.hasOnlyDefaultVariant ? '' : variant.title;
        }
      }

      /* ---------- gallery ---------- */
      bindGallery() {
        this.querySelectorAll('[data-thumb-media-id]').forEach((thumb) => {
          thumb.addEventListener('click', () => this.activateMedia(thumb.dataset.thumbMediaId));
        });
      }

      activateMedia(mediaId) {
        const items = this.querySelectorAll('[data-media-id]');
        let found = false;
        items.forEach((item) => {
          const on = item.dataset.mediaId === String(mediaId);
          if (on) found = true;
          item.classList.toggle('on', on);
          if (!on) {
            const video = item.querySelector('video');
            if (video) video.pause();
          }
        });
        if (!found) return;
        this.querySelectorAll('[data-thumb-media-id]').forEach((thumb) => {
          thumb.classList.toggle('on', thumb.dataset.thumbMediaId === String(mediaId));
        });
      }

      /* ---------- sticky bar ---------- */
      bindSticky() {
        if (!this.sticky || !this.atcButton) return;
        const update = () => {
          const passed = this.atcButton.getBoundingClientRect().bottom < 0;
          this.sticky.classList.toggle('show', passed);
          this.sticky.setAttribute('aria-hidden', String(!passed));
          this.sticky.inert = !passed;
        };
        window.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });
        update();
      }

      /* ---------- back in stock notify ---------- */
      bindNotify() {
        if (!this.notify) return;
        const form = this.notify.querySelector('[data-cii-notify-form]');
        const status = this.notify.querySelector('[data-cii-notify-status]');
        if (!form) return;
        form.addEventListener('submit', (evt) => {
          evt.preventDefault();
          const input = form.querySelector('input[name="whatsapp"]');
          const button = form.querySelector('button[type="submit"]');
          const number = input ? input.value.trim() : '';
          if (number.replace(/\D/g, '').length < 9) {
            if (status) {
              status.textContent = 'Please enter a valid WhatsApp number.';
              status.className = 'cii-notify__status cii-notify__status--err';
            }
            return;
          }
          if (button) button.disabled = true;
          fetch(this.notify.dataset.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              whatsapp: number,
              handle: this.notify.dataset.productHandle,
              product: this.notify.dataset.productTitle,
              variantId: this.notify.dataset.variantId || '',
              variantLabel: this.notify.dataset.variantLabel || '',
              imageUrl: this.notify.dataset.imageUrl || '',
            }),
          })
            .then((res) => res.json().then((body) => ({ ok: res.ok, body })))
            .then(({ ok, body }) => {
              if (status) {
                if (ok && body && body.ok) {
                  status.textContent = 'Done. We will WhatsApp you the moment it is back.';
                  status.className = 'cii-notify__status cii-notify__status--ok';
                  form.querySelector('.cii-notify__row').hidden = true;
                } else {
                  status.textContent = (body && body.error) || 'Something went wrong. Please try again.';
                  status.className = 'cii-notify__status cii-notify__status--err';
                }
              }
            })
            .catch(() => {
              if (status) {
                status.textContent = 'Could not reach us right now. Please try again.';
                status.className = 'cii-notify__status cii-notify__status--err';
              }
            })
            .finally(() => {
              if (button) button.disabled = false;
            });
        });
      }
    }
  );
}

// ---------- V3.1: click-to-zoom on PDP gallery images ----------
(function () {
  function openZoom(overlay, src) {
    var img = overlay.querySelector('.cii-zoom__img');
    if (img.src !== src) img.src = src;
    overlay.hidden = false;
    document.documentElement.style.overflow = 'hidden';
  }
  function closeZoom(overlay) {
    overlay.hidden = true;
    document.documentElement.style.overflow = '';
  }
  document.addEventListener('click', function (e) {
    var item = e.target.closest('.cii-gal__item[data-zoom-src]');
    if (item) {
      var overlay = item.closest('.cii-gal') && item.closest('.cii-gal').querySelector('[data-cii-zoom]');
      if (overlay) openZoom(overlay, item.dataset.zoomSrc);
      return;
    }
    var zoom = e.target.closest('[data-cii-zoom]');
    if (zoom && (e.target === zoom || e.target.closest('[data-cii-zoom-close]'))) closeZoom(zoom);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    document.querySelectorAll('[data-cii-zoom]:not([hidden])').forEach(closeZoom);
  });
})();
